/* global swal, $ */
import {Controller} from "@hotwired/stimulus"

const MILLIS_PER_DAY = 24 * 60 * 60 * 1000
const DAYS_PER_WEEK = 7
const CONFIRM_TEXT = "Weet je zeker dat je de planning wilt publiceren?"
const END_BEFORE_START_TEXT = "De einddatum moet op of na de startdatum liggen."

export default class extends Controller {
  static values = {
    weeksThreshold: {type: Number, default: 3},
    maxWeeks: {type: Number, default: 6},
  }
  static targets = ["startDate", "endDate", "submit", "error"]

  // The date pickers are left unconstrained on purpose: a user may publish any 6-week
  // window, however far in the future. Both range rules — end-before-start and the
  // max-weeks cap — are surfaced live below (inline error + disabled submit) and enforced
  // authoritatively server-side in PublishPlanningValidation.
  connect() {
    this.boundValidate = this.validate.bind(this)
    // Delegated on the form: functions.js runs `$('.datepicker').unbind()` on every
    // Turbolinks/AJAX re-init, which would strip a handler bound on the input itself.
    // The form is never unbound, and jQuery-triggered datepicker `change` events bubble
    // up to delegated handlers — so this catches both calendar picks and manual typing.
    $(this.element).on("change.publishConfirm input.publishConfirm", ".datepicker", this.boundValidate)
    this.validate()
  }

  disconnect() {
    $(this.element).off(".publishConfirm")
  }

  // Live-toggles the inline error and the submit button as either date changes, so the user
  // learns the range is invalid (end before start, or wider than the cap) without pressing
  // Publiceren. Returns the blocking message, or null when the range is valid.
  validate() {
    const error = this.rangeError()

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = error || ""
      this.errorTarget.style.display = error ? "" : "none"
    }

    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = Boolean(error)
    }

    return error
  }

  confirmSubmit(event) {
    event.preventDefault()

    // Defensive guard: the submit button is disabled while the range is invalid, but Enter
    // could still submit the form. Block silently — the inline error is already visible.
    if (this.validate()) return

    if (this.dateRangeExceedsWeeks(this.weeksThresholdValue)) {
      swal(Object.assign({}, window.sweetAlertConfirmConfig, {title: CONFIRM_TEXT}))
        .then((confirmed) => {
          if (confirmed) this.element.submit()
        })
        .catch(() => {})
    } else {
      this.element.submit()
    }
  }

  // The blocking range error, or null when valid. End-before-start takes precedence over
  // the max-weeks cap.
  rangeError() {
    if (this.endBeforeStart()) return END_BEFORE_START_TEXT
    if (this.dateRangeExceedsWeeks(this.maxWeeksValue)) {
      return `Je kunt maximaal ${this.maxWeeksValue} weken tegelijk publiceren.`
    }
    return null
  }

  endBeforeStart() {
    const startDate = this.parseDate(this.startDateTarget.value)
    const endDate = this.parseDate(this.endDateTarget.value)
    if (!startDate || !endDate) return false
    return endDate < startDate
  }

  dateRangeExceedsWeeks(weeks) {
    const startDate = this.parseDate(this.startDateTarget.value)
    const endDate = this.parseDate(this.endDateTarget.value)
    if (!startDate || !endDate) return false
    const days = Math.round((endDate - startDate) / MILLIS_PER_DAY)
    return days > weeks * DAYS_PER_WEEK
  }

  // Parses dd-mm-yyyy (Dutch date format used by the datepicker)
  parseDate(value) {
    const parts = value.split("-")
    if (parts.length < 3) return null
    return new Date(parts[2], parts[1] - 1, parts[0])
  }
}
